import './chat.web.css';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { usePathname, useRouter } from 'expo-router';

import { ChatConversationRow } from '@/components/chat/ChatConversationRow.web';
import { SidebarItem } from '@/components/layout/SidebarItem.web';
import { ChatRenameDialog } from '@/components/chat/ChatRenameDialog.web';
import { Button, Text } from '@/components/ui';
import { createConversation, listConversations, patchConversation } from '@/lib/chat/api';
import {
  CONVERSATION_SECTION_LABELS,
  groupConversationsByTime,
} from '@/lib/chat/conversation-groups';
import { getArchivedChatIds, getPinnedChatIds } from '@/lib/chat/conversation-prefs';
import { CHAT_CONVERSATIONS_CHANGED } from '@/lib/chat/events';
import { setLastChatId } from '@/lib/chat/last-conversation';
import type { Conversation } from '@/lib/chat/types';
import { isOnline } from '@/lib/connectivity';
import { cn } from '@/lib/design-system/cn';

type ChatConversationListProps = {
  activeConversationId?: string | null;
  onNavigate?: () => void;
  variant?: 'sidebar' | 'main';
};

function sortByUpdatedDesc(rows: Conversation[]): Conversation[] {
  return [...rows].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function ChatConversationList({
  activeConversationId,
  onNavigate,
  variant = 'sidebar',
}: ChatConversationListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [allConversations, setAllConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [prefsVersion, setPrefsVersion] = useState(0);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Conversation | null>(null);
  const [renameSaving, setRenameSaving] = useState(false);
  const offline = !isOnline();

  const load = useCallback(async () => {
    if (!isOnline()) {
      setLoading(false);
      setAllConversations([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setAllConversations(await listConversations());
    } catch (err) {
      setAllConversations([]);
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, pathname]);

  useEffect(() => {
    function onChanged() {
      setPrefsVersion((value) => value + 1);
      void load();
    }
    window.addEventListener(CHAT_CONVERSATIONS_CHANGED, onChanged);
    return () => window.removeEventListener(CHAT_CONVERSATIONS_CHANGED, onChanged);
  }, [load]);

  const { activeConversations, archivedConversations, pinnedConversations, timeGroups } =
    useMemo(() => {
      const archivedIds = getArchivedChatIds();
      const pinnedIds = getPinnedChatIds();
      const active = allConversations.filter((row) => !archivedIds.has(row.id));
      const archived = sortByUpdatedDesc(
        allConversations.filter((row) => archivedIds.has(row.id)),
      );
      const pinned = sortByUpdatedDesc(active.filter((row) => pinnedIds.has(row.id)));
      const unpinned = active.filter((row) => !pinnedIds.has(row.id));
      return {
        activeConversations: active,
        archivedConversations: archived,
        pinnedConversations: pinned,
        timeGroups: groupConversationsByTime(unpinned),
      };
    }, [allConversations, prefsVersion]);

  function bumpPrefs() {
    setPrefsVersion((value) => value + 1);
    void load();
  }

  async function onNewChat() {
    if (offline || creating) {
      return;
    }
    setCreating(true);
    try {
      const conversation = await createConversation();
      setLastChatId(conversation.id);
      onNavigate?.();
      router.push(`/chat/${conversation.id}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  }

  function openConversation(id: string) {
    setLastChatId(id);
    onNavigate?.();
    router.push(`/chat/${id}`);
  }

  async function saveRename(title: string) {
    if (!renameTarget) {
      return;
    }
    setRenameSaving(true);
    try {
      await patchConversation(renameTarget.id, { title: title || null });
      setRenameTarget(null);
      bumpPrefs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename conversation');
    } finally {
      setRenameSaving(false);
    }
  }

  function renderRow(conversation: Conversation, archived = false) {
    return (
      <ChatConversationRow
        key={conversation.id}
        active={activeConversationId === conversation.id}
        archived={archived}
        conversation={conversation}
        onChanged={bumpPrefs}
        onOpen={openConversation}
        onRenameRequest={setRenameTarget}
      />
    );
  }

  const hasMainList =
    pinnedConversations.length > 0 || timeGroups.length > 0 || activeConversations.length === 0;

  return (
    <View
      className={cn(
        'flex flex-col',
        variant === 'main' ? 'min-h-0 flex-1 px-page py-page-y' : 'min-h-0 flex-1 px-0.5 py-0.5',
      )}
    >
      <View className={cn('mb-2 shrink-0', variant === 'main' ? '' : 'px-1')}>
        <Button
          label="New chat"
          loading={creating}
          disabled={offline}
          size="sm"
          variant="secondary"
          onPress={() => void onNewChat()}
        />
      </View>

      <View className="min-h-0 flex-1 flex-col overflow-y-auto">
        {offline ? (
          <Text variant="caption" className="px-1.5 text-left text-muted">
            Chat requires an internet connection.
          </Text>
        ) : null}

        {loading ? (
          <View className="items-center py-4">
            <ActivityIndicator size="small" />
          </View>
        ) : error ? (
          <Text variant="caption" className="px-1.5 text-left text-danger">
            {error}
          </Text>
        ) : !hasMainList && archivedConversations.length === 0 ? (
          <Text variant="caption" className="px-1.5 text-left text-muted">
            No conversations yet.
          </Text>
        ) : (
          <View className="flex flex-col gap-2 pb-2">
            {pinnedConversations.length > 0 ? (
              <View className="flex flex-col gap-0.5">
                {pinnedConversations.map((conversation) => renderRow(conversation))}
              </View>
            ) : null}

            {timeGroups.map((group) => (
              <View key={group.section} className="flex flex-col gap-0.5">
                <Text variant="caption" className="chat-conversation-section-label px-1.5">
                  {CONVERSATION_SECTION_LABELS[group.section]}
                </Text>
                {group.conversations.map((conversation) => renderRow(conversation))}
              </View>
            ))}
          </View>
        )}
      </View>

      {!loading && archivedConversations.length > 0 ? (
        <View className="mt-auto shrink-0 border-t border-border pt-1">
          <SidebarItem
            canDuplicate={false}
            canRename={false}
            canTrash={false}
            depth={0}
            hasChildPages
            headerToggleOnly
            isActive={false}
            isExpanded={archivedExpanded}
            label={`Archived chats (${archivedConversations.length})`}
            onDuplicate={() => {}}
            onPress={() => setArchivedExpanded((value) => !value)}
            onRename={() => {}}
            onToggleExpand={() => setArchivedExpanded((value) => !value)}
            onTrash={() => {}}
            variant="section"
          />
          {archivedExpanded ? (
            <View className="mt-1 flex flex-col gap-0.5">
              {archivedConversations.map((conversation) => renderRow(conversation, true))}
            </View>
          ) : null}
        </View>
      ) : null}

      <ChatRenameDialog
        initialTitle={renameTarget?.title?.trim() || 'New conversation'}
        open={renameTarget != null}
        saving={renameSaving}
        onClose={() => {
          if (!renameSaving) {
            setRenameTarget(null);
          }
        }}
        onSave={saveRename}
      />
    </View>
  );
}
